import React, { useState } from 'react';
import { X, Download, Upload, RefreshCw, Trash2, Copy, Check, AlertTriangle, ExternalLink } from 'lucide-react';
import { useCalendarSync } from '../../hooks/useCalendarSync';

const PROVIDER_OPTIONS = [
  { value: 'google',  label: 'Google Calendar' },
  { value: 'outlook', label: 'Outlook' },
  { value: 'apple',   label: 'Apple iCloud' },
  { value: 'other',   label: 'Outro' },
];

const SYNC_STATUS_META = {
  success: { label: 'OK',     color: 'text-emerald-600' },
  error:   { label: 'erro',   color: 'text-red-600' },
};

function formatDateTime(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return value;
  }
}

export default function CalendarSyncSettings({ open, onClose, colaboradorId, onAfterSync }) {
  const sync = useCalendarSync(colaboradorId);
  const [newUrl, setNewUrl] = useState('');
  const [newProvider, setNewProvider] = useState('google');
  const [newName, setNewName] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [generatedUrl, setGeneratedUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const showFeedback = (kind, msg) => {
    setFeedback({ kind, msg });
    setTimeout(() => setFeedback(null), 5000);
  };

  const handleAdd = async () => {
    setBusy(true);
    const { error } = await sync.addFeed({ feedUrl: newUrl, provider: newProvider, calendarName: newName });
    setBusy(false);
    if (error) { showFeedback('error', error); return; }
    setNewUrl('');
    setNewName('');
    showFeedback('ok', 'Feed adicionado. Clique em "Sincronizar" para importar eventos.');
  };

  const handleRemove = async (id) => {
    setBusy(true);
    const { error } = await sync.removeFeed(id);
    setBusy(false);
    if (error) showFeedback('error', error);
    else showFeedback('ok', 'Feed removido.');
  };

  const handleSyncAll = async () => {
    setBusy(true);
    const { data, error } = await sync.syncFeeds();
    setBusy(false);
    if (error) { showFeedback('error', error); return; }
    showFeedback('ok', `${data?.events_upserted ?? 0} eventos sincronizados, ${data?.events_removed ?? 0} removidos.`);
    onAfterSync?.();
  };

  const handleGenerate = async () => {
    setBusy(true);
    const { url, error } = await sync.generateToken();
    setBusy(false);
    if (error) { showFeedback('error', error); return; }
    setGeneratedUrl(url);
    setCopied(false);
    showFeedback('ok', 'Link gerado. Copie agora — não será exibido novamente.');
  };

  const handleRevoke = async () => {
    setBusy(true);
    const { error } = await sync.revokeToken();
    setBusy(false);
    setConfirmRevoke(false);
    if (error) { showFeedback('error', error); return; }
    setGeneratedUrl(null);
    showFeedback('ok', 'Link revogado.');
  };

  const handleCopy = async () => {
    if (!generatedUrl) return;
    try {
      await navigator.clipboard.writeText(generatedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      showFeedback('error', 'Não foi possível copiar — selecione o texto manualmente.');
    }
  };

  const inputClass =
    'w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[10001] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <RefreshCw className="text-blue-600" size={20} />
            Sincronização de calendário
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={22} />
          </button>
        </div>

        <div className="p-5 space-y-6">
          {feedback && (
            <div className={`text-sm border rounded p-2 ${feedback.kind === 'ok' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-700'}`}>
              {feedback.msg}
            </div>
          )}

          {!colaboradorId && (
            <div className="text-sm bg-amber-50 border border-amber-200 rounded p-3 text-amber-800 flex items-start gap-2">
              <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
              <div>Seu usuário não está mapeado para um colaborador conhecido. Logue como eduardo / fabio / caroline para usar a sincronização.</div>
            </div>
          )}

          {/* Seção 1 — Importar */}
          <section>
            <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
              <Download size={14} className="text-blue-600" /> Importar meu calendário
            </h3>
            <p className="text-xs text-slate-500 mb-3">
              Cole a URL secreta do seu Google Calendar ou Outlook para que reuniões pessoais apareçam no /agenda como read-only.
              {' '}<a href="https://support.google.com/calendar/answer/37648?hl=pt-BR#zippy=%2Cobter-o-link-particular-para-um-calend%C3%A1rio" target="_blank" rel="noreferrer" className="text-blue-700 hover:underline inline-flex items-center gap-0.5">Como encontrar? <ExternalLink size={10} /></a>
            </p>

            <div className="grid grid-cols-12 gap-2 mb-2">
              <input
                type="url"
                value={newUrl}
                onChange={(e) => setNewUrl(e.target.value)}
                placeholder="https://calendar.google.com/calendar/ical/.../basic.ics"
                className={`col-span-7 ${inputClass}`}
              />
              <select
                value={newProvider}
                onChange={(e) => setNewProvider(e.target.value)}
                className={`col-span-3 ${inputClass}`}
              >
                {PROVIDER_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
              <button
                onClick={handleAdd}
                disabled={busy || !newUrl.trim() || !colaboradorId}
                className="col-span-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                Adicionar
              </button>
            </div>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Nome do calendário (opcional, ex: Pessoal Eduardo)"
              className={inputClass}
            />

            <div className="mt-4">
              {sync.loading && <div className="text-xs text-slate-500">Carregando feeds...</div>}
              {!sync.loading && sync.feeds.length === 0 && (
                <div className="text-xs text-slate-500 italic">Nenhum feed cadastrado.</div>
              )}
              {sync.feeds.length > 0 && (
                <>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs text-slate-500">{sync.feeds.length} feed{sync.feeds.length > 1 ? 's' : ''}</span>
                    <button
                      onClick={handleSyncAll}
                      disabled={busy || sync.syncing}
                      className="flex items-center gap-1.5 px-2.5 py-1 text-xs bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 rounded-lg disabled:opacity-50"
                    >
                      <RefreshCw size={12} className={sync.syncing ? 'animate-spin' : ''} /> {sync.syncing ? 'Sincronizando...' : 'Sincronizar agora'}
                    </button>
                  </div>
                  <ul className="space-y-1.5">
                    {sync.feeds.map((f) => {
                      const meta = SYNC_STATUS_META[f.last_sync_status] || { label: f.last_sync_status || '—', color: 'text-slate-500' };
                      return (
                        <li key={f.id} className="flex items-start gap-2 bg-slate-50 border border-slate-200 rounded px-2.5 py-2 text-xs">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-slate-700">{f.calendar_name || f.provider}</span>
                              <span className="text-slate-400">·</span>
                              <span className="text-slate-500">{f.provider}</span>
                              {f.events_count != null && (
                                <span className="text-slate-500">· {f.events_count} eventos</span>
                              )}
                            </div>
                            <div className="text-slate-500 truncate" title={f.feed_url}>{f.feed_url}</div>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-slate-500">Último sync:</span>
                              <span className={meta.color}>{meta.label}</span>
                              <span className="text-slate-500">{formatDateTime(f.last_synced_at)}</span>
                            </div>
                            {f.last_sync_error && (
                              <div className="text-red-600 truncate" title={f.last_sync_error}>Erro: {f.last_sync_error}</div>
                            )}
                          </div>
                          <button
                            onClick={() => handleRemove(f.id)}
                            disabled={busy}
                            className="text-slate-400 hover:text-red-600 p-1"
                            title="Remover feed (apaga eventos importados)"
                          >
                            <Trash2 size={14} />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
            </div>
          </section>

          {/* Seção 2 — Exportar */}
          <section className="border-t border-slate-200 pt-5">
            <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
              <Upload size={14} className="text-blue-600" /> Exportar agenda REIP
            </h3>
            <p className="text-xs text-slate-500 mb-3">
              Gere um link secreto para assinar a agenda REIP no Google Calendar, Outlook ou no celular.
              {' '}<strong>Black Box:</strong> apenas título genérico e horário saem — sem dados comerciais, sem nomes de empresa, sem valores.
            </p>

            {!generatedUrl && !sync.activeToken && (
              <button
                onClick={handleGenerate}
                disabled={busy || !colaboradorId}
                className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5"
              >
                <Upload size={14} /> Gerar link de assinatura
              </button>
            )}

            {generatedUrl && (
              <div className="space-y-2">
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-2">
                  <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                  Copie agora. Por segurança, este link não será exibido novamente — se perder, gere outro (o anterior será revogado).
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={generatedUrl}
                    onClick={(e) => e.currentTarget.select()}
                    className={`${inputClass} font-mono text-xs`}
                  />
                  <button
                    onClick={handleCopy}
                    className="px-3 py-2 text-sm bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-lg flex items-center gap-1.5"
                  >
                    {copied ? <><Check size={14} className="text-emerald-600" /> Copiado</> : <><Copy size={14} /> Copiar</>}
                  </button>
                </div>
              </div>
            )}

            {!generatedUrl && sync.activeToken && (
              <div className="bg-slate-50 border border-slate-200 rounded p-3 text-xs space-y-2">
                <div className="text-slate-700">
                  Existe um link ativo desde {formatDateTime(sync.activeToken.created_at)}
                  {sync.activeToken.last_accessed_at && ` · último acesso ${formatDateTime(sync.activeToken.last_accessed_at)}`}
                  {sync.activeToken.access_count != null && ` · ${sync.activeToken.access_count} acessos`}.
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={handleGenerate}
                    disabled={busy}
                    className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    Gerar novo (revoga o atual)
                  </button>
                  {!confirmRevoke ? (
                    <button
                      onClick={() => setConfirmRevoke(true)}
                      className="px-3 py-1.5 text-xs bg-white border border-red-300 text-red-700 hover:bg-red-50 rounded-lg"
                    >
                      Revogar
                    </button>
                  ) : (
                    <button
                      onClick={handleRevoke}
                      disabled={busy}
                      className="px-3 py-1.5 text-xs bg-red-600 text-white hover:bg-red-700 rounded-lg disabled:opacity-50"
                    >
                      Confirmar revogação
                    </button>
                  )}
                </div>
              </div>
            )}
          </section>
        </div>

        <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-700 bg-white border border-slate-300 hover:bg-slate-100 rounded-lg"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
